import { CardElement } from '@stripe/react-stripe-js';

import React, { Fragment, PureComponent } from 'react';

import styled from 'styled-components';

import { _getPurchasedSubscriptions } from '@redux/reducers/profile/selectors';

import { compose } from 'redux';

import Box from '../atoms/Box/Box';
import Button from '../atoms/Button/Button';
import { Col, Row } from '../atoms/Grid';
import Image from '../atoms/Image/Image';
import Loader from '../atoms/Loader/Loader';
import Text from '../atoms/Text/Text';
import CheckoutInput from '../molecules/CheckoutInput/CheckoutInput';
import PoweredByStripe from '../molecules/PoweredByStripe/PoweredByStripe';
import RenewalPrice from '../molecules/RenewalPrice';
import StatusWrapper from '../molecules/StatusWrapper/StatusWrapper';

import { ROUTE } from '@utils/index';

import { localizedMoment } from '@utils/localizedMoment';

import { Align } from '@shared/enums/align';
import { B2C_EDU } from '@shared/subscription/constants';
import { Bundle } from '@shared/enums/bundle';
import { CARD_TYPE, DATE_FORMAT, Status } from '@shared/markup/constants';
import CenterMessage from './CenterMessage/CenterMessage';
import CheckoutStripeCard from './CheckoutStripeCard/CheckoutStripeCard';
import { Color } from '@shared/enums/button-color';
import { I18nTranslation } from '@shared/interfaces/i18n';
import Router from 'next/router';
import { Size } from '@shared/enums/button-size';
import { WithStripe } from '@hoc/WithStripe';
import { withFormik } from 'formik';
import { withTranslation } from 'i18n';
import { object as yupObject, string as yupString } from 'yup';

const BoxLink = styled(Box)`
  cursor: pointer;
`;

const StyledButton = styled(Button)`
  max-width: 80% !important;
  padding-left: 1em;
  padding-right: 1em;
  border: 2px solid ${({ theme }) => theme.colors.abyss};
  color: ${({ theme }) => theme.colors.abyss};
  font-weight: 700;
`;

type ManageSubscriptionProps = {
  bundle: Bundle;
  last4: string;
  cardBrand: string;
  month: number;
  year: number;
  hasActive: boolean;
  hasBillingRetry: boolean;
  toggleOffAutoRenewSwitch: (bundle: Bundle) => void;
  onClickSwitchAutoRenew: (
    bundle: Bundle,
    hasActive: boolean,
    hasBillingRetry: boolean,
    isApplicableOffer: boolean
  ) => void;
  loading: boolean;
  stripe: object;
  handleSubmit: () => void;
  onSubmit: () => void;
  statusEditCard: { type: string; message: string };
  showEditCard: string | boolean;
  showAutoRenewWarning: string | boolean;
  showBillingRetryWarning: string | boolean;
  showSubscriptionError: boolean;
  handleChange: () => void;
  values: { name: string };
  errors: { name: string };
  touched: { name: string };
  onClickGetOffer: (bundle: Bundle) => void;
  offerDiscount: number;
  offerCurrentPrice?: string | number;
  offerPrice?: string | number;
  isAcceptedOffer: boolean;
  isApplicableOffer: boolean;
  isGettingOffer: boolean;
  onCloseAutoRenewWarning: () => void;
  onShowEditCardPopup: (bundle: Bundle) => void;
  onCloseEditCardPopup: () => void;
  onCloseBillingRetryWarning: () => void;
  expireDate: string;
  t: I18nTranslation;
};

class ManageSubscription extends PureComponent<ManageSubscriptionProps> {
  handleToggleOffAutoRenewSwitch = () => {
    this.props.toggleOffAutoRenewSwitch(this.props.bundle);
  };

  goToSubscriptionCancelPage = () => {
    localStorage.setItem('expireDate', this.props.expireDate);
    localStorage.setItem('bundle', this.props.bundle.toLowerCase());
    Router.push(ROUTE.CANCELLATION.FEEDBACK);
  };

  handleClickSwitchAutoRenew = () => {
    this.props.onClickSwitchAutoRenew(
      this.props.bundle,
      this.props.hasActive,
      this.props.hasBillingRetry,
      this.props.isApplicableOffer
    );
  };

  handleGetOffer = () => {
    this.props.onClickGetOffer(this.props.bundle);
  };

  handleShowEditCardPopup = () => {
    this.props.onShowEditCardPopup(this.props.bundle);
  };

  renderRenewalButton = () => {
    const {
      bundle,
      hasActive,
      hasBillingRetry,
      loading,
      expireDate,
      t: tProp,
    } = this.props;

    const hideForB2C = bundle.toLowerCase().includes(B2C_EDU);

    if (loading || hideForB2C) return null;

    const t = (key, options?) => tProp(`subs.${key}`, options);
    const formattedExpireDate = localizedMoment(expireDate, DATE_FORMAT);

    const hasChecked = hasActive || hasBillingRetry;

    const cancel = (
      <StyledButton
        onClick={
          hasBillingRetry
            ? this.handleClickSwitchAutoRenew
            : this.goToSubscriptionCancelPage
        }
        sizeType={Size.Ninth}
      >
        {t('cancelRenewal')}
      </StyledButton>
    );

    const reactivate = (
      <Fragment>
        <Button
          colorType={Color.Abyss}
          sizeType={Size.Ninth}
          onClick={this.handleClickSwitchAutoRenew}
        >
          {t('reactivateMyRenewal')}
        </Button>
        <Box mt={0.5}>
          <Text>{t('nextBilling', { date: formattedExpireDate })}</Text>
        </Box>
      </Fragment>
    );

    return hasChecked ? cancel : reactivate;
  };

  render() {
    const {
      cardBrand,
      last4,
      month,
      year,
      loading,
      handleSubmit,
      handleChange,
      values,
      errors,
      touched,
      offerDiscount,
      isAcceptedOffer,
      showAutoRenewWarning,
      bundle,
      onCloseAutoRenewWarning,
      offerCurrentPrice,
      offerPrice,
      isGettingOffer,
      showBillingRetryWarning,
      onCloseBillingRetryWarning,
      showEditCard,
      onCloseEditCardPopup,
      statusEditCard,
      showSubscriptionError,
      t: tProp,
    } = this.props;

    const t = (key, options?) => tProp(`subs.${key}`, options);

    let cardImage = null;
    if (cardBrand) {
      cardImage = CARD_TYPE[cardBrand];
    }

    let cardExpiration = null;
    if (month && year) {
      let formattedMonth = month < 10 ? '0' + month : month;

      let formattedYear = year.toString().slice(-2);

      cardExpiration = formattedMonth + '/' + formattedYear;
    }

    return (
      <Fragment>
        <Box>
          <Row>
            {last4 && (
              <Col xs={12} sm={6}>
                <Box mb={0.25}>
                  <Row middle="xs">
                    <Col>
                      {cardImage && (
                        <Image
                          width={2.75}
                          height={1.625}
                          src={cardImage.src}
                          srcSet={cardImage.srcSet}
                        />
                      )}
                    </Col>
                    <Col>
                      <Text
                        size={1.125}
                        lineHeight="normal"
                        color="cornflowerBlue"
                      >
                        {`${cardBrand} ${t('cardBrandEnd')} ${last4}`}
                      </Text>
                    </Col>
                  </Row>
                </Box>
                <BoxLink onClick={this.handleShowEditCardPopup}>
                  {cardExpiration && (
                    <ExpireText size={1.125}>
                      {t('cardExpires', { date: cardExpiration })}
                    </ExpireText>
                  )}
                  <EditText size={1.125} lineHeight="normal" color="abyss">
                    {t('edit')}
                  </EditText>
                </BoxLink>
              </Col>
            )}
            <Col xs={12} sm={6}>
              <Box smMt={1.625}>
                {loading && <Loader key="key1" size={3} />}
                {this.renderRenewalButton()}
              </Box>
              {showSubscriptionError && (
                <Box mt={0.5}>
                  <Text color="scarlet">{t('reqFailed')}</Text>
                </Box>
              )}
            </Col>
          </Row>
        </Box>

        {showAutoRenewWarning === bundle && (
          <CenterMessage
            smPl={0}
            smPr={0}
            mPl={0}
            mPr={0}
            hasBorder={false}
            onClose={onCloseAutoRenewWarning}
          >
            <Box
              smMt={1}
              smMb={0.625}
              smMl={1}
              smMr={1}
              mb={1}
              ml={1.875}
              mr={1.875}
            >
              <Text
                smSize={2}
                size={2.25}
                color="cornflowerBlue"
                lineHeight="normal"
                weight="bold"
              >
                {t('offerDiscount', { offerDiscount })}
              </Text>
            </Box>
            <Image
              width="100%"
              src="https://cdn.3d4medical.com/store/images/account-page/bg.jpg"
              srcSet={[
                'https://cdn.3d4medical.com/store/images/account-page/bg.jpg',
                'https://cdn.3d4medical.com/store/images/account-page/bg@2x.jpg',
                'https://cdn.3d4medical.com/store/images/account-page/bg@3x.jpg',
              ]}
            />
            <Box
              pl={1}
              pr={1}
              smMt={0.625}
              smMb={0.625}
              mt={1}
              mb={1}
              align={Align.Center}
            >
              <Text
                smSize={1.125}
                size={1.25}
                color="cornflowerBlue"
                lineHeight={1.5}
                weight="bold"
              >
                {t('oneDayOffer', { offerDiscount })}
              </Text>
            </Box>
            <Box smMb={0.625} mb={1}>
              {isAcceptedOffer ? (
                <Box height={5.375} />
              ) : (
                <RenewalPrice oldPrice={offerCurrentPrice} price={offerPrice} />
              )}
            </Box>
            <Box smPl={1} smPr={1} pl={1.875} pr={1.875}>
              <Box smMb={1.25} mb={1.875} align={Align.Center}>
                <Text
                  smSize={1.125}
                  size={1.25}
                  color="cornflowerBlue"
                  lineHeight={1.5}
                >
                  {t('cancelRenewalWarning')}
                </Text>
              </Box>
              <Row>
                <Col xs={12} sm={6}>
                  <Box smMb={1}>
                    {isAcceptedOffer ? (
                      <Button
                        colorType={Color.FullGreen}
                        sizeType={Size.Fourth}
                        disabled
                      >
                        {t('applied')}
                      </Button>
                    ) : (
                      <Button
                        colorType={Color.FullGreen}
                        sizeType={Size.Fourth}
                        onClick={this.handleGetOffer}
                        disabled={loading || isGettingOffer}
                      >
                        {isGettingOffer ? (
                          <Loader color="white" size={1} />
                        ) : (
                          t('getTheOffer')
                        )}
                      </Button>
                    )}

                    <Box mdMt={0.375} align={Align.Center}>
                      <Text size={0.75} color="olsoGray">
                        {t('autoRenew')}
                      </Text>
                    </Box>
                  </Box>
                </Col>
                <Col xs={12} sm={6}>
                  <Button
                    onClick={this.goToSubscriptionCancelPage}
                    colorType={Color.FullRed}
                    sizeType={Size.Fourth}
                    disabled={loading || isGettingOffer}
                  >
                    {loading ? <Loader color="white" size={1} /> : t('cancel')}
                  </Button>
                  <Box mdMt={0.375} align={Align.Center}>
                    <Text size={0.75} color="olsoGray">
                      {t('loseAccess')}
                    </Text>
                  </Box>
                </Col>
              </Row>
            </Box>
          </CenterMessage>
        )}

        {showBillingRetryWarning === bundle && (
          <CenterMessage onClose={onCloseBillingRetryWarning}>
            <Box mb={0.75} smMt={1.5}>
              <Text
                smSize={2}
                size={2.25}
                color="cornflowerBlue"
                lineHeight="normal"
                weight="bold"
              >
                {t('beingBilled')}
              </Text>
            </Box>
            <Box smMb={1.875} mb={2.5}>
              <Text
                smSize={1.125}
                size={1.5}
                color="cornflowerBlue"
                lineHeight={1.5}
                dangerouslySetInnerHTML={{ __html: t('turnOffWarning') }}
              />
            </Box>
            <Row>
              <Col xs={12} sm={6}>
                <Box smMb={0.625}>
                  <Button
                    onClick={this.goToSubscriptionCancelPage}
                    colorType={Color.FullRed}
                    sizeType={Size.Fourth}
                    disabled={loading}
                  >
                    {t('turnOff')}
                  </Button>
                </Box>
              </Col>
              <Col xs={12} sm={6}>
                <Button
                  colorType={Color.FullTransparentBorderGreen}
                  sizeType={Size.Fourth}
                  onClick={onCloseBillingRetryWarning}
                >
                  {t('cancel')}
                </Button>
              </Col>
            </Row>
          </CenterMessage>
        )}

        {showEditCard === bundle && (
          <CenterMessage onClose={onCloseEditCardPopup}>
            <form noValidate onSubmit={handleSubmit}>
              <Box mb={2} smMt={1.5}>
                <Row middle="xs">
                  <Col xs={12} sm={8}>
                    <Text
                      smSize={2}
                      size={2.25}
                      color="cornflowerBlue"
                      lineHeight="normal"
                      weight="bold"
                    >
                      {t('updateDetails')}
                    </Text>
                  </Col>
                  <Col>
                    <Box smMt={0.5}>
                      <PoweredByStripe />
                    </Box>
                  </Col>
                </Row>
              </Box>

              <Box smMb={1.75} mb={2}>
                <CheckoutInput
                  type="text"
                  label={t('nameOnCard')}
                  name="name"
                  error={errors.name && t(errors.name)}
                  touched={touched.name}
                  onChange={handleChange}
                  value={values.name}
                  autoComplete="cc-name"
                />
              </Box>

              <Box smMb={1.75} mb={2}>
                <CheckoutStripeCard label={t('cardDetails')} />
              </Box>

              {!!Object.keys(statusEditCard).length && (
                <Box mt={2} mb={2}>
                  <Text
                    smSize={1}
                    size={1.25}
                    lineHeight="normal"
                    color={
                      statusEditCard.type === 'success' ? 'silverTree' : 'monza'
                    }
                  >
                    {statusEditCard.message}
                  </Text>
                </Box>
              )}

              <Row gutterXs={0.25} gutter={0.625}>
                <Col xs={6}>
                  <Box smMb={0.75}>
                    <StatusWrapper
                      status={loading ? Status.Loading : Status.Nothing}
                      loaderSize={2.75}
                    >
                      <Button
                        colorType={Color.FullGreen}
                        sizeType={Size.Fourth}
                        type="submit"
                      >
                        {t('saveChanges')}
                      </Button>
                    </StatusWrapper>
                  </Box>
                </Col>
                <Col xs={6}>
                  <Button
                    onClick={onCloseEditCardPopup}
                    colorType={Color.FullTransparentBorderGreen}
                    sizeType={Size.Fourth}
                  >
                    {t('cancel')}
                  </Button>
                </Col>
              </Row>
            </form>
          </CenterMessage>
        )}
      </Fragment>
    );
  }
}

const ExpireText = styled(Text)`
  color: ${({ theme }) => theme.colors.grayChateau};
`;

const EditText = styled(Text)`
  padding-left: 20%;
  text-transform: uppercase;
  text-decoration: underline;
`;

const formikConfig = {
  mapPropsToValues: () => {
    return {
      name: '',
    };
  },
  validationSchema: yupObject().shape({
    name: yupString().required('requiredField'),
  }),
  async handleSubmit(_, { props }) {
    const { stripe, elements, onSubmit } = props;
    const cardElement = elements.getElement(CardElement);

    stripe.createToken(cardElement).then(response => {
      if (!response.error) {
        onSubmit(stripe, cardElement);
      }
    });
  },
};

export default WithStripe(
  compose(
    withTranslation('account'),
    withFormik(formikConfig)
  )(ManageSubscription)
);
